import { z } from 'zod';
import { BOT_PLANS, BOT_PRICE_MODES, botPersonaSchema } from '../bot/types';
import { Currency, UnitType } from '../enums';
import { isValidTimezone } from '../funnel/dates';

const thresholdSchema = z.object({
  green: z.number().min(0).max(1),
  amber: z.number().min(0).max(1),
});

export const botSettingsSchema = z.object({
  /** Mensajes máximos sin respuesta del cliente antes de pasar a SIN_RESPUESTA. */
  maxAttempts: z.number().int().min(1).max(20).default(3),
  /** Ventana en horas dentro de la que se reparten esos intentos. */
  attemptWindowHours: z.number().int().min(1).max(720).default(48),
  /** Franja de silencio nocturno (HH:mm); null = el bot escribe a cualquier hora. */
  quietFrom: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .default('22:00'),
  quietTo: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .default('08:00'),
  /** Minutos sin gestión del asesor tras los cuales el bot retoma el lead. */
  resumeAfterMinutes: z.number().int().min(1).max(1440).default(30),
  // --- 8.5 cerebro del bot (ADR-020) ---
  /** Plan de la empresa: Básico sin IA, Estándar, Premium. Lo fija el operador de la plataforma. */
  plan: z.enum(BOT_PLANS).default('ESTANDAR'),
  /** Tope mensual de gasto en modelos (USD) y qué hacer al llegar. */
  monthlyBudgetUsd: z.number().min(0).max(100_000).default(50),
  onBudgetExceeded: z.enum(['BASICO', 'PAUSAR']).default('BASICO'),
  persona: botPersonaSchema.default({}),
  /** DESDE = precios "desde" por tipología; NO = nunca da precios. */
  priceMode: z.enum(BOT_PRICE_MODES).default('DESDE'),
  /** Turnos máximos por conversación antes de escalar al asesor. */
  maxTurns: z.number().int().min(2).max(50).default(12),
  /** Duración de la cita que el bot agenda (minutos). */
  appointmentMinutes: z.number().int().min(15).max(240).default(60),
  /** El bot dice que es un asistente virtual. */
  disclose: z.boolean().default(true),
  // --- 8.5b el bot conversa por WhatsApp ---
  /** Apagado por defecto: el bot no escribe a clientes reales hasta que un administrador lo encienda. */
  active: z.boolean().default(false),
  /** Plantilla con la que el bot abre la conversación cuando el cliente no escribió primero (fuera de las 24 h). */
  firstContactTemplateId: z.string().uuid().nullable().default(null),
});
export type BotSettingsPatch = Partial<z.input<typeof botSettingsSchema>>;

export const companySettingsSchema = z.object({
  /** Códigos de catalog_medium_categories que cuentan como LEAD digital. */
  digitalCategories: z.array(z.string()).default([]),
  /** LEADS_PAUTA = suma de estas categorías. */
  pautaCategories: z.array(z.string()).default([]),
  /** LEADS_PORTALES = suma de estas categorías. */
  portalCategories: z.array(z.string()).default([]),
  /** Tipos de unidad que cuentan en KPIs de separaciones/ventas. */
  countableUnitTypes: z
    .array(z.nativeEnum(UnitType))
    .default([UnitType.CASA, UnitType.DEPARTAMENTO]),
  /** Motivos de caída técnicos (se excluyen de métricas). */
  technicalDropReasons: z.array(z.string()).default([]),
  firstContactSlaMinutes: z.number().int().positive().default(15),
  /** 7.20: al registrar un lead (o un recontacto sin tarea) se crea la tarea «Primer contacto» con fecha = captación + SLA. */
  autoFirstTask: z.boolean().default(true),
  firstTaskType: z.enum(['LLAMADA', 'WHATSAPP']).default('LLAMADA'),
  /** 7.27: al registrar a mano, rechaza a una persona que ya tiene un lead activo en la empresa (importaciones y formularios siguen entrando como recontacto). */
  blockDuplicateManualLeads: z.boolean().default(false),
  businessHours: z
    .object({
      start: z.string().regex(/^\d{2}:\d{2}$/),
      end: z.string().regex(/^\d{2}:\d{2}$/),
      /** 0 = domingo … 6 = sábado */
      days: z.array(z.number().int().min(0).max(6)),
    })
    .default({ start: '09:00', end: '19:00', days: [1, 2, 3, 4, 5, 6] }),
  /** Zona horaria de negocio (IANA): días, meses, SLA y vencimientos se calculan en ella. */
  timezone: z
    .string()
    .refine(isValidTimezone, 'Zona horaria inválida (usa un nombre IANA, p. ej. America/Lima)')
    .default('America/Lima'),
  /** Moneda por defecto de proformas y procesos. */
  currency: z.nativeEnum(Currency).default(Currency.USD),
  /** Tipo de cambio para expresar tickets en USD en el perfil del comprador. */
  penPerUsd: z.number().positive().max(100).default(3.75),
  requirePriorSeparationForSale: z.boolean().default(true),
  semaphore: z
    .object({
      /**
       * Umbral del **% contacto efectivo** del Embudo y el ranking (gestiones efectivas ÷ gestiones de contacto).
       * La clave conserva su nombre histórico para no migrar `companies.settings` (Lote 5 · H4, P28); la pantalla
       * la etiqueta «% contacto efectivo».
       */
      contactabilidad: thresholdSchema,
      citasConcretadas: thresholdSchema,
      visitasSobreCaptaciones: thresholdSchema,
      separacionesSobreVisitas: thresholdSchema,
      ventasSobreCaptaciones: thresholdSchema,
    })
    .default({
      contactabilidad: { green: 0.6, amber: 0.4 },
      citasConcretadas: { green: 0.5, amber: 0.3 },
      visitasSobreCaptaciones: { green: 0.08, amber: 0.05 },
      separacionesSobreVisitas: { green: 0.15, amber: 0.08 },
      ventasSobreCaptaciones: { green: 0.01, amber: 0.005 },
    }),
  /** Patrones (mayúsculas) que marcan un correo como de prueba. */
  testEmailPatterns: z.array(z.string()).default(['TEST@FB.COM']),
  /** Meta Lead Ads: token de página y proyecto por formulario. */
  metaIntegration: z
    .object({
      pageAccessToken: z.string().optional().nullable(),
      pageIds: z.array(z.string()).default([]),
      /** form_id → projectId */
      formProjects: z.record(z.string()).default({}),
      defaultProjectId: z.string().uuid().optional().nullable(),
    })
    .optional()
    .nullable(),
  /** WhatsApp Cloud API: números (phone_number_id) que entran a esta empresa y proyecto por defecto. */
  whatsappIntegration: z
    .object({
      phoneNumberIds: z.array(z.string()).default([]),
      defaultProjectId: z.string().uuid().optional().nullable(),
    })
    .optional()
    .nullable(),
  /** Bot de atención (Fase 8): límites que el bot lee y que el CRM aplica al retomar. */
  bot: botSettingsSchema.default({}),
});

/** Ajustes del bot (límites de la 8.1 + cerebro de la 8.5); se exporta aparte para editarlos por partes. */

export type CompanySettings = z.infer<typeof companySettingsSchema>;
export type BotSettings = CompanySettings['bot'];
export type SemaphoreThreshold = z.infer<typeof thresholdSchema>;
